// =============================================================================
// RegionSection.js — Phase 82
//
// Region + display preferences. Two authoritative preferences are backend-
// wired:
//   • Headline currency (base_currency) via lib/currency.setBaseCurrency —
//     triggers full aggregate refresh across Net Worth / FI / Clarity Score.
// The remainder (number format, date format, week start, timezone) are
// display-only preferences persisted to localStorage until we have
// authoritative backend columns for them. This is intentional — see PRD
// Phase 6C.
// =============================================================================
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { CURRENCIES } from '@/lib/format';
import { setBaseCurrency } from '@/lib/currency';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  SectionHeader, SettingsCard, PrimaryButton, FieldLabel, SelectInput, SavedFlash,
} from './primitives';

const DATE_FORMATS = [
  { value: 'dd-mmm-yyyy', label: '15 Jul 2026' },
  { value: 'dd/mm/yyyy',  label: '15/07/2026' },
  { value: 'mm/dd/yyyy',  label: '07/15/2026' },
  { value: 'yyyy-mm-dd',  label: '2026-07-15' },
];
const WEEK_STARTS = [
  { value: 'Monday',   label: 'Monday' },
  { value: 'Sunday',   label: 'Sunday' },
  { value: 'Saturday', label: 'Saturday' },
];
const NUMBER_FORMATS = [
  { v: 'indian',        label: 'Indian system',        example: '₹12,34,567' },
  { v: 'international', label: 'International system', example: '₹1,234,567' },
  { v: 'abbreviated',   label: 'Abbreviated',          example: '₹12.3L / ₹1.2M' },
];
const COUNTRIES = [
  { value: 'India',           label: 'India' },
  { value: 'United Kingdom',  label: 'United Kingdom' },
  { value: 'United States',   label: 'United States' },
  { value: 'Singapore',       label: 'Singapore' },
  { value: 'Australia',       label: 'Australia' },
  { value: 'United Arab Emirates', label: 'United Arab Emirates' },
];
const TIMEZONES = [
  { value: 'Asia/Kolkata',     label: 'IST — India Standard Time' },
  { value: 'Europe/London',    label: 'GMT — London' },
  { value: 'America/New_York', label: 'ET — New York' },
  { value: 'Asia/Singapore',   label: 'SGT — Singapore' },
  { value: 'Australia/Sydney', label: 'AEST — Sydney' },
];

const LS_KEY = 'twoone20:regionPrefs';

function loadLocalPrefs() {
  try {
    return JSON.parse(window.localStorage.getItem(LS_KEY) || '{}') || {};
  } catch { return {}; }
}
function saveLocalPrefs(next) {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore private-mode */ }
}

export default function RegionSection() {
  const { profile, session, refreshProfile } = useAuth();
  const confirm = useConfirm();

  const [baseDraft, setBaseDraft] = useState(profile?.base_currency || profile?.currency || 'INR');
  const [baseSaving, setBaseSaving] = useState(false);

  const [country, setCountry]       = useState(profile?.country || 'India');
  const [timezone, setTimezone]     = useState('Asia/Kolkata');
  const [numFormat, setNumFormat]   = useState('indian');
  const [dateFormat, setDateFormat] = useState('dd-mmm-yyyy');
  const [weekStart, setWeekStart]   = useState('Monday');
  const [prefsSaved, setPrefsSaved] = useState(false);

  useEffect(() => {
    const p = loadLocalPrefs();
    if (p.timezone)   setTimezone(p.timezone);
    if (p.numFormat)  setNumFormat(p.numFormat);
    if (p.dateFormat) setDateFormat(p.dateFormat);
    if (p.weekStart)  setWeekStart(p.weekStart);
  }, []);

  useEffect(() => {
    setBaseDraft((profile?.base_currency || profile?.currency || 'INR').toUpperCase());
    if (profile?.country) setCountry(profile.country);
  }, [profile?.base_currency, profile?.currency, profile?.country]);

  const handleBaseCurrencyChange = async (newBase) => {
    const current = (profile?.base_currency || profile?.currency || 'INR').toUpperCase();
    if (newBase === current) return;
    const ok = await confirm({
      title: `Change your headline currency to ${newBase}?`,
      description: `Your Net Worth, projections, FI tracker and Clarity Score will be recomputed in ${newBase}. Your transactions and account balances stay exactly as they are, only the headline currency changes.`,
      confirmLabel: `Switch to ${newBase}`,
      cancelLabel: 'Keep current',
    });
    if (!ok) { setBaseDraft(current); return; }
    setBaseSaving(true);
    const res = await setBaseCurrency(session?.access_token, newBase);
    setBaseSaving(false);
    if (res?.ok) {
      toast.success(`Headline currency set to ${newBase}.`);
      await refreshProfile?.();
    } else {
      toast.error(res?.error || 'Could not change base currency.');
      setBaseDraft(current);
    }
  };

  const savePrefs = () => {
    saveLocalPrefs({ timezone, numFormat, dateFormat, weekStart });
    setPrefsSaved(true);
    setTimeout(() => setPrefsSaved(false), 2500);
    toast.success('Display preferences saved.');
  };

  return (
    <div className="space-y-5" data-testid="settings-region-section">
      <SectionHeader
        title="Region & Currency"
        subtitle="Control how numbers, dates, and currencies appear throughout 2120."
      />

      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">Location</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="Country" />
            <SelectInput value={country} onChange={setCountry} options={COUNTRIES} testId="settings-country-select" />
          </div>
          <div>
            <FieldLabel label="Timezone" />
            <SelectInput value={timezone} onChange={setTimezone} options={TIMEZONES} testId="settings-timezone-select" />
          </div>
        </div>
      </SettingsCard>

      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">Currency display</p>
        <div className="space-y-4">
          <div>
            <FieldLabel
              label={`Headline currency${baseSaving ? ' · Saving…' : ''}`}
              hint="Primary currency used across all summaries and charts. Change triggers a full aggregate refresh."
            />
            <SelectInput
              value={baseDraft}
              onChange={(v) => { setBaseDraft(v); handleBaseCurrencyChange(v); }}
              disabled={baseSaving}
              options={CURRENCIES.map((c) => ({ value: c.code, label: `${c.symbol} ${c.code}` }))}
              testId="settings-base-currency-select"
            />
          </div>
          <div>
            <FieldLabel label="Number format" hint="Controls how large numbers are displayed. Applies on next page load." />
            <div className="space-y-2 mt-2">
              {NUMBER_FORMATS.map((opt) => {
                const active = numFormat === opt.v;
                return (
                  <label key={opt.v} className="flex items-center gap-3 cursor-pointer group">
                    <span
                      onClick={() => setNumFormat(opt.v)}
                      className="w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        borderColor: active ? 'hsl(var(--evergreen))' : 'hsl(var(--border))',
                        backgroundColor: active ? 'hsl(var(--evergreen))' : 'transparent',
                      }}
                      data-testid={`settings-number-format-${opt.v}`}
                    >
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </span>
                    <span className="flex-1">
                      <span className="text-sm text-charcoal">{opt.label}</span>
                      <span className="text-xs text-muted-foreground ml-2">{opt.example}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard className="px-6 py-6">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-5">Date &amp; week</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FieldLabel label="Date format" />
            <SelectInput value={dateFormat} onChange={setDateFormat} options={DATE_FORMATS} testId="settings-date-format-select" />
          </div>
          <div>
            <FieldLabel label="Week starts on" />
            <SelectInput value={weekStart} onChange={setWeekStart} options={WEEK_STARTS} testId="settings-week-start-select" />
          </div>
        </div>
      </SettingsCard>

      <div className="flex items-center gap-3">
        <PrimaryButton onClick={savePrefs} testId="settings-region-save">Save display preferences</PrimaryButton>
        <SavedFlash visible={prefsSaved} />
      </div>
    </div>
  );
}
